import React, { useMemo } from 'react';
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar, ResponsiveContainer } from 'recharts';
import { customColors } from '@utils/gradeUtils.js';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { calculateGradeDistribution } from '@utils/metrics/index.js';

const GradeDistributionChart = React.memo(({
  data,
  climbingStyle,
  gradeSystem,
  formatGradeWithStyle,
  onGradeClick,
  isHorizontal,
  showDistinctRoutes
}) => {
  // Step 1: Process raw data
  const processedData = useMemo(() => {
    if (!data?.length) return [];
    return calculateGradeDistribution(data, climbingStyle, showDistinctRoutes);
  }, [data, climbingStyle, showDistinctRoutes]);

  // Step 2: Transform for display (sorting, filling in missing grades, layout)
  const displayData = useMemo(() => {
    if (!processedData.length) return [];

    // Find the min and max grades in the data
    const grades = processedData.map(item => item.grade);
    const minGrade = Math.min(...grades);
    const maxGrade = Math.max(...grades);
    
    // Create a complete array with all grades between min and max
    const completeGradeRange = [];
    for (let grade = minGrade; grade <= maxGrade; grade++) {
      // Find existing data for this grade or create empty data
      const existingGradeData = processedData.find(item => item.grade === grade);
      
      if (existingGradeData) {
        completeGradeRange.push(existingGradeData);
      } else {
        // Create an empty grade entry with all ascent types set to 0
        const emptyGradeData = { grade };
        
        // Initialize all ascent types based on climbing style
        if (climbingStyle === CLIMBING_STYLES.BOULDER) {
          emptyGradeData.onsight = 0;
          emptyGradeData.flash = 0;
          emptyGradeData.send = 0;
          emptyGradeData.repeat = 0;
          emptyGradeData.attempts = 0;
        } else {
          emptyGradeData.onsight = 0;
          emptyGradeData.flash = 0;
          emptyGradeData.redpoint = 0;
          emptyGradeData.pinkpoint = 0;
          emptyGradeData.firstAscent = 0;
          emptyGradeData.attempts = 0;
        }
        
        completeGradeRange.push(emptyGradeData);
      }
    }
    
    // Sort by grade
    return completeGradeRange.sort((a, b) => a.grade - b.grade);
  }, [processedData, climbingStyle]);

  // Step 3: Calculate display properties
  const chartProperties = useMemo(() => {
    if (!displayData.length) return {
      hasPinkPoints: false,
      hasFirstAscents: false,
      maxValue: 0,
      bottomMargin: 22,
      leftMargin: 0
    };

    return {
      hasPinkPoints: displayData.some(d => d.pinkpoint > 0),
      hasFirstAscents: displayData.some(d => d.firstAscent > 0),
      maxValue: Math.max(...displayData.map(d => 
        Object.values(d)
          .filter(val => typeof val === 'number' && val !== d.grade)
          .reduce((sum, val) => sum + val, 0)
      )),
      bottomMargin: isHorizontal ? 22 : (gradeSystem === 'ewbank' ? 22 : 42),
      leftMargin: isHorizontal ? (gradeSystem === 'ewbank' ? -12 : 12) : 0
    };
  }, [displayData, isHorizontal, gradeSystem]);

  // Create memoized tick formatter to ensure it updates with gradeSystem changes
  const memoizedTickFormatter = useMemo(() => {
    return (value) => formatGradeWithStyle(value, gradeSystem);
  }, [formatGradeWithStyle, gradeSystem]);

  // Step 4: Handle empty state
  if (!displayData.length) {
    return <div className="text-center py-8">No data available</div>;
  }

  // Step 5: Render chart
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart 
        data={isHorizontal ? [...displayData].reverse() : displayData}
        margin={{ 
          top: 0, 
          right: 0, 
          left: chartProperties.leftMargin, 
          bottom: chartProperties.bottomMargin
        }}
        onClick={onGradeClick}
        layout={isHorizontal ? "vertical" : "horizontal"}
      >
        <CartesianGrid strokeDasharray="3 3" />
        {isHorizontal ? (
          <>
            <XAxis 
              type="number"
              label={{ 
                value: showDistinctRoutes ? 'Number of Routes' : 'Number of Ascents',
                position: 'bottom',
                offset: 0
              }}
            />
            <YAxis
              type="category"
              dataKey="grade"
              tickFormatter={memoizedTickFormatter}
              label={{ 
                value: `Grade (${climbingStyle === CLIMBING_STYLES.BOULDER ? 'V-Grade' : gradeSystem})`,
                angle: -90,
                position: 'middle',
                dx: gradeSystem === 'ewbank' ? -10 : -35
              }}
              interval={0}
              tick={{ fontSize: 12 }}
            />
          </>
        ) : (
          <>
            <XAxis 
              dataKey="grade"
              type="category"
              label={{ 
                value: `Grade (${climbingStyle === CLIMBING_STYLES.BOULDER ? 'V-Grade' : gradeSystem})`,
                position: 'bottom',
                offset: gradeSystem === 'ewbank' ? 8 : 28
              }}
              tickFormatter={memoizedTickFormatter}
              interval={0}
              angle={gradeSystem === 'ewbank' ? 0 : -45}
              textAnchor={gradeSystem === 'ewbank' ? "middle" : "end"}
            />
            <YAxis
              label={{ 
                value: showDistinctRoutes ? 'Number of Routes' : 'Number of Ascents',
                angle: -90,
                position: 'middle',
                dx: -20
              }}
            />
          </>
        )}
        <Tooltip 
          labelFormatter={memoizedTickFormatter}
          cursor={{ fill: 'transparent' }}
        />
        <Legend 
          align="right" 
          verticalAlign="middle" 
          layout="vertical"
          wrapperStyle={{ 
            paddingLeft: "10px",
          }}
        />
        <Bar 
          animationDuration={300}
          dataKey="onsight" 
          name="Onsight" 
          stackId="stack" 
          fill={customColors.onsight} 
          cursor="pointer" 
        />
        <Bar 
          animationDuration={300}
          dataKey="flash" 
          name="Flash" 
          stackId="stack" 
          fill={customColors.flash} 
          cursor="pointer" 
        />
        
        {climbingStyle === CLIMBING_STYLES.BOULDER ? (
          <>
            <Bar 
              animationDuration={300}
              dataKey="send" 
              name="Send" 
              stackId="stack" 
              fill={customColors.send} 
              cursor="pointer" 
            />
            <Bar 
              animationDuration={300}
              dataKey="repeat" 
              name="Repeat" 
              stackId="stack" 
              fill={customColors.repeat} 
              cursor="pointer" 
            />
          </>
        ) : (
          <>
            <Bar 
              animationDuration={300}
              dataKey="redpoint" 
              name="Redpoint" 
              stackId="stack" 
              fill={customColors.redpoint} 
              cursor="pointer" 
            />
            {chartProperties.hasPinkPoints && (
              <Bar 
                animationDuration={300}
                dataKey="pinkpoint" 
                name="Pink Point" 
                stackId="stack" 
                fill={customColors.pinkpoint} 
                cursor="pointer" 
              />
            )}
            {chartProperties.hasFirstAscents && (
              <Bar 
                animationDuration={300}
                dataKey="firstAscent" 
                name="First Ascent" 
                stackId="stack" 
                fill={customColors.redpoint} 
                cursor="pointer" 
              />
            )}
          </>
        )}
        
        <Bar 
          animationDuration={300}
          dataKey="attempts" 
          name="Attempts" 
          stackId="stack" 
          fill={customColors.attempts} 
          cursor="pointer" 
        />
      </BarChart>
    </ResponsiveContainer>
  );
});

GradeDistributionChart.displayName = 'GradeDistributionChart';

export default GradeDistributionChart;